/**
 * Shared workspace status hook — consolidates the polling that RepoPanel
 * and DeployPanel used to do independently.
 *
 * BEFORE: RepoPanel polled `/api/import/status?workspace_id=…` every 3s
 * AND DeployPanel polled `/api/workspaces/{id}/deploy/plan` every 5s.
 * The two parallel loops caused UI flicker (each panel could see the
 * workspace as "empty" for one tick while the other saw it as ready),
 * duplicated backend load, and produced the "botones desaparecen"
 * bug reported by Emergent Support in Feb 2026.
 *
 * AFTER: this module runs a SINGLE combined poll per workspaceId,
 * broadcasts to every subscriber, and shares a stable snapshot. Both
 * panels re-render off the same source of truth.
 */
import { useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api';

export interface WorkspaceStatusSnapshot {
  importStatus: any | null;
  deployPlan: any | null;
  runtimeStatus: any | null;
  buildStatus: any | null;
  indexStatus: any | null;
  loading: boolean;
  lastError: string | null;
  lastUpdated: number;
}

type Listener = (snap: WorkspaceStatusSnapshot) => void;

interface Entry {
  snap: WorkspaceStatusSnapshot;
  listeners: Set<Listener>;
  timer: ReturnType<typeof setInterval> | null;
  inFlight: boolean;
}

const REGISTRY: Map<string, Entry> = new Map();

const EMPTY: WorkspaceStatusSnapshot = {
  importStatus: null,
  deployPlan: null,
  runtimeStatus: null,
  buildStatus: null,
  indexStatus: null,
  loading: true,
  lastError: null,
  lastUpdated: 0,
};

async function pollOnce(workspaceId: string, entry: Entry): Promise<void> {
  if (entry.inFlight) return; // dedupe concurrent ticks
  entry.inFlight = true;
  try {
    // Parallel fan-out but ALL sourced from this single hook.
    // .catch(() => null) so a single-endpoint failure never blanks the panel.
    const [importStatus, deployPlan, runtimeStatus, buildStatus, indexStatus] = await Promise.all([
      apiService.importStatus(workspaceId).catch(() => entry.snap.importStatus),
      apiService.getDeployPlan(workspaceId).catch(() => entry.snap.deployPlan),
      apiService.runtimeStatus(workspaceId).catch(() => entry.snap.runtimeStatus),
      apiService.getBuildStatus(workspaceId).catch(() => entry.snap.buildStatus),
      apiService.workspaceIndexStatus(workspaceId).catch(() => entry.snap.indexStatus),
    ]);
    entry.snap = {
      importStatus,
      deployPlan,
      runtimeStatus,
      buildStatus,
      indexStatus,
      loading: false,
      lastError: null,
      lastUpdated: Date.now(),
    };
    entry.listeners.forEach((l) => {
      try { l(entry.snap); } catch { /* listener bug — do not break the loop */ }
    });
  } catch (err: any) {
    entry.snap = {
      ...entry.snap,
      loading: false,
      lastError: String(err?.message || err),
      lastUpdated: Date.now(),
    };
    entry.listeners.forEach((l) => l(entry.snap));
  } finally {
    entry.inFlight = false;
  }
}

function getEntry(workspaceId: string): Entry {
  let e = REGISTRY.get(workspaceId);
  if (!e) {
    e = { snap: { ...EMPTY }, listeners: new Set(), timer: null, inFlight: false };
    REGISTRY.set(workspaceId, e);
  }
  return e;
}

/** React hook — every component that reads workspace status uses this. */
export function useWorkspaceStatus(
  workspaceId: string | null | undefined,
  intervalMs: number = 4000,
): WorkspaceStatusSnapshot & { refresh: () => Promise<void> } {
  const [snap, setSnap] = useState<WorkspaceStatusSnapshot>(EMPTY);
  const wsRef = useRef(workspaceId);
  wsRef.current = workspaceId;

  useEffect(() => {
    if (!workspaceId) return;
    const entry = getEntry(workspaceId);
    const listener: Listener = (s) => setSnap(s);
    entry.listeners.add(listener);
    // Immediately serve the cached snap so mount doesn't flash `loading`.
    if (entry.snap.lastUpdated > 0) setSnap(entry.snap);
    // Kick off a fresh tick right away — every panel expects fresh data on mount.
    pollOnce(workspaceId, entry);
    // Ensure exactly ONE interval per workspace, shared by all subscribers.
    if (!entry.timer) {
      entry.timer = setInterval(() => {
        void pollOnce(workspaceId, entry);
      }, Math.max(1500, intervalMs));
    }
    return () => {
      entry.listeners.delete(listener);
      // If nobody is listening anymore, stop polling to conserve resources.
      if (entry.listeners.size === 0 && entry.timer) {
        clearInterval(entry.timer);
        entry.timer = null;
      }
    };
  }, [workspaceId, intervalMs]);

  const refresh = async () => {
    if (!wsRef.current) return;
    const entry = getEntry(wsRef.current);
    await pollOnce(wsRef.current, entry);
  };

  return { ...snap, refresh };
}
